import { spacing } from '../style';
import { mobileFirstBreakPoints } from '../style/device';
import { getCssRuleWithResponsiveCalculation } from './get-css-rule-with-responsive-calc';

// 'xs', 'sm', 'md'...
type SpacingKeys = keyof typeof spacing;
// '48px', '56px', '64px'...
type SpacingValuesInPixels = (typeof spacing)[SpacingKeys];

type MapSpacingPropsToValuesReturnValue =
  | undefined
  | SpacingValuesInPixels
  | { [key in keyof typeof mobileFirstBreakPoints]?: SpacingValuesInPixels };

// this function is used to map the spacing props to the values in pixels
// So it receives an object that looks like this: { xs: 'lg', sm: 'lg', md: 'sm' }
// and returns an object that looks like this: { xs: '32px', sm: '32px', md: '16px' }
// if it receives a string it returns the value of the string from the spacing object.
// So if it receives the string 'lg' it returns '32px'
export const mapSpacingPropsToValues = (
  value?: string | { [key in keyof typeof mobileFirstBreakPoints]?: keyof typeof spacing },
): MapSpacingPropsToValuesReturnValue => {
  if (!value || Object.keys(value).length === 0) {
    return;
  }

  // the value can be a string - that will be most cases if the developer doesnt need responsive calculations
  if (typeof value === 'string') {
    return spacing[value as keyof typeof spacing];
  }

  // this is the responsive use case. when the developer passes an object.
  // the final dict is supposed to look like this: { xs: '32px', sm: '32px', md: '16px' }
  const objectWithValueInPixels: { [key in keyof typeof mobileFirstBreakPoints]?: SpacingValuesInPixels } = {};

  const valueDeviceKeys = Object.keys(value) as Array<keyof typeof mobileFirstBreakPoints>;
  valueDeviceKeys.forEach(key => {
    // spacingKey is supposed to look like this: 'xs'
    const spacingKey = value[key];
    if (spacingKey) {
      objectWithValueInPixels[key] = spacing[spacingKey];
    }
  });

  return objectWithValueInPixels;
};

export const withSpacing = (props: any) => [
  getCssRuleWithResponsiveCalculation({
    property: 'margin-right',
    value: mapSpacingPropsToValues(props.marginRight) || mapSpacingPropsToValues(props.marginHorizontal),
  }),
  getCssRuleWithResponsiveCalculation({
    property: 'margin-left',
    value: mapSpacingPropsToValues(props.marginLeft) || mapSpacingPropsToValues(props.marginHorizontal),
  }),
  getCssRuleWithResponsiveCalculation({
    property: 'margin-top',
    value: mapSpacingPropsToValues(props.marginTop) || mapSpacingPropsToValues(props.marginVertical),
  }),
  getCssRuleWithResponsiveCalculation({
    property: 'margin-bottom',
    value: mapSpacingPropsToValues(props.marginBottom) || mapSpacingPropsToValues(props.marginVertical),
  }),
  getCssRuleWithResponsiveCalculation({
    property: 'margin',
    value: mapSpacingPropsToValues(props.margin),
  }),
  getCssRuleWithResponsiveCalculation({
    property: 'padding-right',
    value: mapSpacingPropsToValues(props.paddingRight) || mapSpacingPropsToValues(props.paddingHorizontal),
  }),
  getCssRuleWithResponsiveCalculation({
    property: 'padding-left',
    value: mapSpacingPropsToValues(props.paddingLeft) || mapSpacingPropsToValues(props.paddingHorizontal),
  }),
  getCssRuleWithResponsiveCalculation({
    property: 'padding-top',
    value: mapSpacingPropsToValues(props.paddingTop) || mapSpacingPropsToValues(props.paddingVertical),
  }),
  getCssRuleWithResponsiveCalculation({
    property: 'padding-bottom',
    value: mapSpacingPropsToValues(props.paddingBottom) || mapSpacingPropsToValues(props.paddingVertical),
  }),
  getCssRuleWithResponsiveCalculation({
    property: 'padding',
    value: mapSpacingPropsToValues(props.padding),
  }),
];
